import { DictionaryTypeDto, OfferComponentValueTypeDto, OfferDefinitionDto } from '@/api/generated';
import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';

/** An offer definition that can receive the requested entry, with its DICTIONARY items for that type. */
export type ProcessCreateOfferDefinitionOption = SelectSearchOptionModel<number> & {
  items: SelectSearchOptionModel<number>[];
};

/** The offer section state known before the definitions load; only `bookable` loads them. */
export type ProcessCreateOfferPrecondition =
  | { reason: 'calendarsDisabled' | 'inactiveEntry'; status: 'noBooking' }
  | { status: 'bookable' | 'hidden' };

export type ProcessCreateOfferPreconditionParams = {
  /** Undefined while the type configuration is unknown. */
  calendarsEnabled?: boolean;
  /** Creating a process with an offer also requires offer creation; without it only the process is saved. */
  canCreateOffer: boolean;
  hasEntryAndRange: boolean;
  isEntryInactive: boolean;
  type?: DictionaryTypeDto;
};

export const processCreateOfferUtil = {
  /**
   * Active definitions with a component of the requested dictionary type and at least one DICTIONARY
   * item; the items are the positions that can receive the entry (ADR 0005: no runtime components).
   */
  definitionOptions: (
    definitions: OfferDefinitionDto[],
    type: DictionaryTypeDto,
  ): ProcessCreateOfferDefinitionOption[] =>
    definitions.flatMap(definition => {
      const items = definition.components
        .filter(component => component.dictionaryType === type)
        .flatMap(component =>
          component.items
            .filter(item => item.valueType === OfferComponentValueTypeDto.Dictionary)
            .flatMap(item =>
              item.offerComponentDefinitionItemId === undefined
                ? []
                : [
                    {
                      label: `${component.name} / ${item.name}`,
                      value: item.offerComponentDefinitionItemId,
                    },
                  ],
            ),
        );
      return definition.active && items.length > 0
        ? [{ items, label: definition.name, value: definition.offerDefinitionId }]
        : [];
    }),
  /** Whether the query allows a rezerwacja wstępna of the entry, and if not, why the section says so. */
  precondition: ({
    calendarsEnabled,
    canCreateOffer,
    hasEntryAndRange,
    isEntryInactive,
    type,
  }: ProcessCreateOfferPreconditionParams): ProcessCreateOfferPrecondition => {
    if (!canCreateOffer || !hasEntryAndRange || !type || calendarsEnabled === undefined) {
      return { status: 'hidden' };
    }
    if (!calendarsEnabled) return { reason: 'calendarsDisabled', status: 'noBooking' };
    if (isEntryInactive) return { reason: 'inactiveEntry', status: 'noBooking' };
    return { status: 'bookable' };
  },
};
